import { Text, ButtonPrimary, ButtonInterface } from '@portals/ui-kit';
import { RtCheckbox } from '@dpa/react-checkbox';
import { RtItem } from '@dpa/react-item';
import { RtFrame } from '@dpa/react-frame-intro';
import { RtText } from '@dpa/react-text';
import { RtFormFieldTextarea } from '@dpa/react-form-field-textarea';
import { Field, Form } from 'react-final-form';
import classnames from 'classnames/bind';
import { useTranslations } from 'next-intl';
import { MIN_RATING_TO_SKIP_FEEDBACK_DETAILS } from '@/_components/article/_components/feedback/_constants';
import { FEEDBACK_MODAL_OPTIONS, OPTION } from './_constants';
import { EFeedbackModalOption, TFeedbackData } from './_types';
import styles from './index.module.scss';
import { FeedbackDrawerWrapper } from '../feedback-drawer-wrapper';

const BLOCK_NAME = 'Feedback-modal';
const cn = classnames.bind(styles);

type TProps = {
  isOpened: boolean;
  onClose: () => void;
  onCloseEvent: () => void;
  onSubmit: (data: TFeedbackData) => void;
  isInDrawer?: boolean;
  rating: number;
};

export const FeedbackModal = ({
  isOpened,
  onClose,
  onCloseEvent,
  onSubmit,
  isInDrawer,
  rating,
}: TProps) => {
  const translations = useTranslations('article.feedbackModal');
  const negativeRate = rating < MIN_RATING_TO_SKIP_FEEDBACK_DETAILS;

  const handleSubmit = (data: TFeedbackData) => {
    onSubmit(data);
    onClose();
  };

  const handleClose = () => {
    onCloseEvent();
    onClose();
  };

  const positiveTitle = translations('positiveTitle');
  const negativeTitle = translations('negativeTitle');
  const textAreaLabel = translations('textArea.label');
  const textAreaPlaceholder = translations('textArea.placeholder');
  const description = translations('description');
  const saveButtonText = translations('controls.save');
  const cancelButtonText = translations('controls.cancel');
  const title = negativeRate ? negativeTitle : positiveTitle;

  const titleText = (
    <Text size="title-l-bold" text={title} whiteSpace="pre-wrap" />
  );

  const content = (
    <div
      className={cn(BLOCK_NAME, { [`${BLOCK_NAME}--in-drawer`]: isInDrawer })}
    >
      {isInDrawer ? (
        titleText
      ) : (
        <div className={cn(`${BLOCK_NAME}__title`)}>{titleText}</div>
      )}

      <div className={cn(`${BLOCK_NAME}__content`)}>
        <Form
          onSubmit={handleSubmit}
          render={({ handleSubmit: handleRenderSubmit, invalid }) => {
            return (
              <RtFrame
                as="form"
                className={cn({
                  [`${BLOCK_NAME}__form`]: isInDrawer,
                })}
                onSubmit={handleRenderSubmit}
              >
                <div>
                  {negativeRate &&
                    FEEDBACK_MODAL_OPTIONS.map(
                      (option: EFeedbackModalOption) => {
                        return (
                          <Field
                            key={option}
                            name={option}
                            id={option}
                            render={({ input }) => (
                              <RtItem
                                label={translations(OPTION[option])}
                                selectorSlot={
                                  <RtCheckbox
                                    modifier="coloring"
                                    isActive={input.value}
                                    name={input.name}
                                    onChange={(event) => {
                                      input.onChange(event.value);
                                    }}
                                    size="md"
                                    use="primary"
                                    value={option}
                                    id={option}
                                  />
                                }
                                size="lg"
                                squeeze
                                use="generic"
                              />
                            )}
                          />
                        );
                      },
                    )}
                  <div className={cn(`${BLOCK_NAME}__text-area`)}>
                    <Field
                      id="comment"
                      name="comment"
                      render={({ input }) => {
                        return (
                          <RtFormFieldTextarea
                            clearable="none"
                            label={textAreaLabel}
                            detailsSpecification="main"
                            modifier="mono"
                            placeholder={textAreaPlaceholder}
                            resize="none"
                            rows="max"
                            phDisplay="persistent"
                            size="lg"
                            ui="normal"
                            use="neutral"
                            name={input.name}
                            onChange={(data) => {
                              input.onChange(data.value);
                            }}
                          />
                        );
                      }}
                    />
                  </div>
                  <div className={cn(`${BLOCK_NAME}__text-area-footer`)}>
                    <RtText specification="main" typography="body-sm-regular">
                      {description}
                    </RtText>
                  </div>
                </div>

                <div className={cn(`${BLOCK_NAME}__controls`)}>
                  <ButtonPrimary
                    dataTestid="feedback-submit"
                    disabled={invalid}
                    htmlType="submit"
                    text={saveButtonText}
                  />
                  <ButtonInterface
                    onClick={handleClose}
                    text={cancelButtonText}
                  />
                </div>
              </RtFrame>
            );
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      {isOpened && (
        <FeedbackDrawerWrapper
          isOpened={isOpened}
          onClose={onClose}
          isInDrawer={isInDrawer}
        >
          {content}
        </FeedbackDrawerWrapper>
      )}
    </>
  );
};
